/**
 * Tour Event Tracking Utility
 * 
 * Provides consistent methods for tracking tour events from the frontend.
 * Follows the same pattern as the quiz tracking implementation.
 */
import { Apii } from '@/services/api.js';

class TourTracking {
    constructor() {
        this.apiEndpoint = '/tour/track-event';
        this.retryAttempts = 2;
        this.retryDelay = 1000; // 1 second
    }

    /**
     * Track a tour event with retry logic and graceful error handling
     * 
     * @param {string} eventType - The type of event to track
     * @param {Object} options - Additional options
     * @param {number} options.step - Step number (0-4)
     * @param {string} options.stepName - Step name (wallet, points, nextcard, course, redemption)
     * @param {string} options.stepId - Step ID (same as stepName)
     * @param {Object} options.metadata - Additional metadata to include
     * @returns {Promise<boolean>} - Returns true if successful, false otherwise
     */
    async trackEvent(eventType, options = {}) {
        const { metadata, ...restOptions } = options;
        const payload = {
            event_type: eventType,
            ...restOptions,
            ...(metadata ? { metadata } : {})
        };

        // Remove undefined values
        Object.keys(payload).forEach(key => {
            if (payload[key] === undefined) {
                delete payload[key];
            }
        });

        return this._makeRequest(payload);
    }

    /**
     * Track when the tour teaser is shown
     */
    async trackTeaserShown() {
        return this.trackEvent('tour_teaser_shown');
    }

    /**
     * Track when the tour teaser is dismissed
     */
    async trackTeaserDismissed() {
        return this.trackEvent('tour_teaser_dismissed');
    }

    /**
     * Track when the tour is started
     */
    async trackTourStarted() {
        return this.trackEvent('tour_started');
    }

    /**
     * Track when a tour step is viewed
     * 
     * @param {number} step - Step number (0-4)
     * @param {string} stepName - Step name
     */
    async trackStepViewed(step, stepName) {
        return this.trackEvent('tour_step_viewed', {
            step,
            step_name: stepName,
            step_id: stepName
        });
    }

    /**
     * Track when a tour step is completed
     * 
     * @param {number} step - Step number (0-4)
     * @param {string} stepName - Step name
     */
    async trackStepCompleted(step, stepName) {
        return this.trackEvent('tour_step_completed', {
            step,
            step_name: stepName,
            step_id: stepName
        });
    }

    /**
     * Track when the tour is cancelled
     * 
     * @param {number} currentStep - Current step when cancelled
     * @param {string} currentStepName - Current step name when cancelled
     */
    async trackTourCancelled(currentStep = null, currentStepName = null) {
        return this.trackEvent('tour_cancelled', this._createStepOptions(currentStep, currentStepName));
    }

    /**
     * Track when the tour is skipped
     * 
     * @param {number} currentStep - Current step when skipped
     * @param {string} currentStepName - Current step name when skipped
     */
    async trackTourSkipped(currentStep = null, currentStepName = null) {
        return this.trackEvent('tour_skipped', this._createStepOptions(currentStep, currentStepName));
    }

    /**
     * Track when the tour is completed
     */
    async trackTourCompleted() {
        return this.trackEvent('tour_completed');
    }

    /**
     * Create step options object from step parameters
     * 
     * @param {number} step - Step number
     * @param {string} stepName - Step name
     * @returns {Object} - Options object
     */
    _createStepOptions(step = null, stepName = null) {
        const options = {};
        
        if (step !== null) {
            options.step = step;
        }
        
        if (stepName !== null) {
            options.step_name = stepName;
            options.step_id = stepName;
        }

        return options;
    }

    /**
     * Make the actual API request with retry logic
     * 
     * @param {Object} payload - The data to send
     * @param {number} attempt - Current attempt number
     * @returns {Promise<boolean>}
     */
    async _makeRequest(payload, attempt = 1) {
        try {
            const ApiService = new Apii();
            await ApiService.apiCall(this.apiEndpoint, 'post', payload);
            return true;
        } catch (error) {
            console.warn(`Tour tracking failed (attempt ${attempt}):`, error.message);
            
            // Retry logic with exponential backoff
            if (attempt < this.retryAttempts) {
                const backoffDelay = this.retryDelay * Math.pow(2, attempt - 1);
                await this._delay(backoffDelay);
                return this._makeRequest(payload, attempt + 1);
            }
            
            // Final failure - log but don't throw
            console.warn('Tour tracking failed after all retry attempts. Continuing silently.');
            return false;
        }
    }

    /**
     * Delay helper for retry logic
     * 
     * @param {number} ms - Milliseconds to delay
     * @returns {Promise}
     */
    _delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// Create and export a singleton instance
const tourTracking = new TourTracking();

export default tourTracking;

// Also export the class for testing purposes
export { TourTracking };
